import { generateClient } from 'aws-amplify/api';
import { type GraphQLResult } from '@aws-amplify/api';
import { ChatModelId } from './mcpService';
import { getCurrentUserOwnerCandidates } from '../utils/ownerScope';

const client = generateClient();

// GraphQL queries and mutations
const CREATE_CONVERSATION = `
  mutation CreateMCPConversation($input: CreateMCPConversationInput!) {
    createMCPConversation(input: $input) {
      id
      title
      model
      modelCodename
      messageCount
      lastMessageAt
      isActive
      conversationType
      projectTaskId
      projectId
      metadata
      createdAt
      updatedAt
    }
  }
`;

const GET_PROJECT_CONVERSATIONS = `
  query MCPConversationsByProjectId($projectId: ID!, $filter: ModelMCPConversationFilterInput, $sortDirection: ModelSortDirection, $limit: Int, $nextToken: String) {
    mCPConversationsByProjectIdAndCreatedAt(projectId: $projectId, filter: $filter, sortDirection: $sortDirection, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        model
        modelCodename
        messageCount
        unreadCount
        lastMessageAt
        isActive
        conversationType
        projectTaskId
        projectId
        metadata
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const GET_USER_CONVERSATIONS = `
  query MCPConversationsByOwner($owner: ID!, $filter: ModelMCPConversationFilterInput, $sortDirection: ModelSortDirection, $limit: Int, $nextToken: String) {
    mCPConversationsByOwnerAndLastMessageAt(owner: $owner, filter: $filter, sortDirection: $sortDirection, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        model
        modelCodename
        messageCount
        unreadCount
        lastMessageAt
        isActive
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const GET_CONVERSATION_WITH_MESSAGES = `
  query GetMCPConversation($id: ID!) {
    getMCPConversation(id: $id) {
      id
      title
      model
      modelCodename
      messageCount
      unreadCount
      lastMessageAt
      isActive
      conversationType
      agentScheduleRunnerId
      messages {
        items {
          id
          content
          isUser
          model
          modelCodename
          executionTimeMs
          toolsCount
          rounds
          serverId
          sessionId
          inputTokens
          outputTokens
          messageRead
          status
          attachments
          rating
          ratingComment
          createdAt
        }
      }
      createdAt
      updatedAt
    }
  }
`;

// Thumbs up/down on an AI response. Only the fields the UI needs to reconcile are selected.
const UPDATE_MESSAGE_RATING = `
  mutation UpdateMCPMessageRating($input: UpdateMCPMessageInput!) {
    updateMCPMessage(input: $input) {
      id
      conversationId
      rating
      ratingComment
    }
  }
`;

const CREATE_MESSAGE = `
  mutation CreateMCPMessage($input: CreateMCPMessageInput!) {
    createMCPMessage(input: $input) {
      id
      conversationId
      content
      isUser
      model
      modelCodename
      enhancedPrompt
      executionTimeMs
      toolsCount
      rounds
      serverId
      sessionId
      inputTokens
      outputTokens
      attachments
      createdAt
    }
  }
`;

const GET_CONVERSATION_MESSAGE_COUNT = `
  query GetMCPConversationMessageCount($id: ID!) {
    getMCPConversation(id: $id) {
      id
      messageCount
    }
  }
`;

const UPDATE_CONVERSATION = `
  mutation UpdateMCPConversation($input: UpdateMCPConversationInput!) {
    updateMCPConversation(input: $input) {
      id
      title
      messageCount
      unreadCount
      lastMessageAt
      updatedAt
    }
  }
`;

// Types
export interface Conversation {
  id: string;
  title: string;
  model: ChatModelId;
  modelCodename: string;
  messageCount: number;
  unreadCount?: number;
  lastMessageAt: string;
  isActive: boolean;
  projectTaskId?: string;
  projectId?: string;
  metadata?: string | Record<string, unknown>;
  conversationType?: string | null; // "chat" | "agent"
  agentScheduleRunnerId?: string | null; // set on scheduled-agent run conversations
  messages?: {
    items: ConversationMessage[];
  };
  createdAt: string;
  updatedAt: string;
}

export interface ConversationMessage {
  id: string;
  conversationId?: string;
  content: string;
  isUser: boolean;
  model?: ChatModelId;
  modelCodename?: string;
  enhancedPrompt?: string;
  executionTimeMs?: number;
  toolsCount?: number;
  rounds?: number;
  serverId?: string;
  sessionId?: string;
  inputTokens?: number;
  outputTokens?: number;
  messageRead?: boolean;
  status?: 'pending' | 'complete' | 'error';
  attachments?: string;
  rating?: MessageRating | null;
  ratingComment?: string | null;
  createdAt: string;
}

export type MessageRating = 'UP' | 'DOWN';

export interface CreateConversationInput {
  id?: string;
  title: string;
  model: ChatModelId;
  modelCodename: string;
  conversationType?: string;
  projectTaskId?: string;
  projectId?: string;
  metadata?: Record<string, unknown>;
}

export interface CreateMessageInput {
  conversationId: string;
  content: string;
  isUser: boolean;
  model?: ChatModelId;
  modelCodename?: string;
  enhancedPrompt?: string;
  executionTimeMs?: number;
  toolsCount?: number;
  rounds?: number;
  serverId?: string;
  sessionId?: string;
  inputTokens?: number;
  outputTokens?: number;
  attachments?: string;
}

// Service class
export class ConversationService {
  // Create a new conversation
  static async createConversation(input: CreateConversationInput): Promise<Conversation> {
    try {
      const result = await client.graphql({
        query: CREATE_CONVERSATION,
        variables: {
          input: {
            ...(input.id && { id: input.id }),
            title: input.title,
            model: input.model,
            modelCodename: input.modelCodename,
            messageCount: 0,
            lastMessageAt: new Date().toISOString(),
            isActive: true,
            ...(input.conversationType && { conversationType: input.conversationType }),
            ...(input.projectTaskId && { projectTaskId: input.projectTaskId }),
            ...(input.projectId && { projectId: input.projectId }),
            ...(input.metadata && { metadata: JSON.stringify(input.metadata) }),
          }
        }
      }) as GraphQLResult<{ createMCPConversation: Conversation }>;

      if (!result.data?.createMCPConversation) {
        throw new Error('Failed to create conversation - no data returned');
      }

      return result.data.createMCPConversation;
    } catch (error) {
      console.error('[ConversationService.createConversation] Error:', error);
      throw error;
    }
  }

  // Get user's conversations (most recent first) via the conversationsByOwner GSI,
  // which returns each owner partition already sorted by lastMessageAt.
  //
  // owner is stored in multiple formats depending on write path (sub, username,
  // sub::username — see src/utils/ownerScope.ts), so we query the index once per
  // candidate format and merge. Querying by own owner also naturally scopes admins,
  // who otherwise have global read on MCPConversation.
  static async getUserConversations(limit: number = 20): Promise<Conversation[]> {
    try {
      const ownerCandidates = await getCurrentUserOwnerCandidates();
      if (ownerCandidates.length === 0) {
        return [];
      }

      const filter = {
        isActive: { eq: true },
        or: [
          { conversationType: { eq: 'chat' } },
          { conversationType: { attributeExists: false } },
        ],
      };

      const perOwner = await Promise.all(
        ownerCandidates.map(async (owner) => {
          const items: Conversation[] = [];
          let nextToken: string | null | undefined = null;

          // The partition is DESC-sorted, so once `limit` items pass the filter
          // everything remaining is older — safe to stop early.
          do {
            const result = await client.graphql({
              query: GET_USER_CONVERSATIONS,
              variables: {
                owner,
                filter,
                sortDirection: 'DESC',
                limit: 100,
                nextToken,
              }
            }) as GraphQLResult<{ mCPConversationsByOwnerAndLastMessageAt: { items: Conversation[]; nextToken?: string | null } }>;

            const page = result.data?.mCPConversationsByOwnerAndLastMessageAt;
            items.push(...(page?.items || []).filter((item) => item?.id).map((item) => ({ ...item, messageCount: item.messageCount ?? 0 })));
            nextToken = page?.nextToken;
          } while (nextToken && items.length < limit);

          return items;
        })
      );

      // Each conversation lives under exactly one owner format, but dedupe
      // defensively, then re-sort across the merged partitions.
      const conversationMap = new Map<string, Conversation>();
      perOwner.flat().forEach((item) => conversationMap.set(item.id, item));

      return Array.from(conversationMap.values())
        .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime())
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  }

  // Get a conversation with all its messages
  static async getConversationWithMessages(conversationId: string): Promise<Conversation | null> {
    try {
      const result = await client.graphql({
        query: GET_CONVERSATION_WITH_MESSAGES,
        variables: { id: conversationId }
      }) as GraphQLResult<{ getMCPConversation: Conversation }>;

      if (!result.data?.getMCPConversation) {
        return null;
      }

      return result.data.getMCPConversation;
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return null;
    }
  }

  // Add a message to a conversation
  static async addMessage(input: CreateMessageInput): Promise<ConversationMessage> {
    try {
      const messageResult = await client.graphql({
        query: CREATE_MESSAGE,
        variables: {
          input: {
            conversationId: input.conversationId,
            content: input.content,
            isUser: input.isUser,
            model: input.model,
            modelCodename: input.modelCodename,
            enhancedPrompt: input.enhancedPrompt,
            executionTimeMs: input.executionTimeMs,
            toolsCount: input.toolsCount,
            rounds: input.rounds,
            serverId: input.serverId,
            sessionId: input.sessionId,
            inputTokens: input.inputTokens,
            outputTokens: input.outputTokens,
            attachments: input.attachments,
          }
        }
      }) as GraphQLResult<{ createMCPMessage: ConversationMessage }>;
      
      if (!messageResult.data?.createMCPMessage) {
        throw new Error('Failed to create message');
      }

      // Update conversation lastMessageAt and increment messageCount
      try {
        // First get current conversation to get the current messageCount
        const getResult = await client.graphql({
          query: GET_CONVERSATION_MESSAGE_COUNT,
          variables: { id: input.conversationId }
        }) as GraphQLResult<{ getMCPConversation: { id: string; messageCount: number } }>;
        
        const currentCount = getResult.data?.getMCPConversation?.messageCount || 0;
        
        // Update with incremented count
        await client.graphql({
          query: UPDATE_CONVERSATION,
          variables: {
            input: {
              id: input.conversationId,
              lastMessageAt: new Date().toISOString(),
              messageCount: currentCount + 1,
            }
          }
        });
      } catch (updateError) {
        console.warn('Failed to update conversation lastMessageAt and messageCount:', updateError);
      }

      return messageResult.data.createMCPMessage;
    } catch (error) {
      console.error('Error adding message:', error);
      throw new Error('Failed to add message');
    }
  }

  // Generate conversation title from first user message
  static generateTitle(firstMessage: string): string {
    const cleanMessage = firstMessage.trim();
    
    // Take first 50 characters and add ellipsis if longer
    if (cleanMessage.length <= 50) {
      return cleanMessage;
    }
    
    // Find the last complete word within 47 characters
    const truncated = cleanMessage.substring(0, 47);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > 20) { // Ensure we have a reasonable amount of text
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    return truncated + '...';
  }

  // Soft delete a conversation
  // Rate an AI response (owner-only via the MCPMessage owner update rule). Passing a comment
  // stores it alongside the rating; omitting it leaves any existing comment untouched.
  static async rateMessage(
    messageId: string,
    rating: MessageRating,
    ratingComment?: string
  ): Promise<void> {
    const input: { id: string; rating: MessageRating; ratingComment?: string } = {
      id: messageId,
      rating,
    };
    if (ratingComment !== undefined) {
      input.ratingComment = ratingComment;
    }
    const result = (await client.graphql({
      query: UPDATE_MESSAGE_RATING,
      variables: { input },
    })) as GraphQLResult<{ updateMCPMessage: { id: string } }>;
    if (!result.data?.updateMCPMessage) {
      throw new Error('Failed to rate message');
    }
  }

  static async deleteConversation(conversationId: string): Promise<void> {
    await client.graphql({
      query: UPDATE_CONVERSATION,
      variables: {
        input: {
          id: conversationId,
          isActive: false,
        }
      }
    });
  }

  // Fetch conversations for a specific project
  //
  // NOTE: This drains every page before slicing to `limit`. We can't stop early
  // because the GSI (mCPConversationsByProjectIdAndCreatedAt) is sorted by
  // `createdAt`, but we display by `lastMessageAt` (sorted below). An old
  // conversation with a recent message sits on a later page yet belongs at the
  // top, so early-stopping would drop it. Fine at current volumes.
  // Let errors propagate — the caller thunk maps them to a failed state so users see an error, not "no chats"
  static async getProjectConversations(projectId: string, limit: number = 50): Promise<Conversation[]> {
    const all: Conversation[] = [];
    let nextToken: string | null | undefined = null;

    do {
      const result = await client.graphql({
        query: GET_PROJECT_CONVERSATIONS,
        variables: {
          projectId,
          filter: { isActive: { eq: true } },
          sortDirection: 'DESC',
          limit: 100,
          nextToken,
        }
      }) as GraphQLResult<{ mCPConversationsByProjectIdAndCreatedAt: { items: Conversation[]; nextToken?: string | null } }>;

      const items = result.data?.mCPConversationsByProjectIdAndCreatedAt?.items || [];
      all.push(...items.filter((item) => item?.id).map((item) => ({ ...item, messageCount: item.messageCount ?? 0 })));
      nextToken = result.data?.mCPConversationsByProjectIdAndCreatedAt?.nextToken;
    } while (nextToken);

    return all
      .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime())
      .slice(0, limit);
  }

  // Mark a conversation as read (reset unreadCount to 0)
  static async markConversationAsRead(conversationId: string): Promise<void> {
    try {
      await client.graphql({
        query: UPDATE_CONVERSATION,
        variables: {
          input: {
            id: conversationId,
            unreadCount: 0,
          }
        }
      });
    } catch (error) {
      console.error('Error marking conversation as read:', error);
      throw new Error('Failed to mark conversation as read');
    }
  }

  // Rename a conversation — title is already owner-updatable, so no schema change needed
  static async renameConversation(conversationId: string, title: string): Promise<void> {
    try {
      await client.graphql({
        query: UPDATE_CONVERSATION,
        variables: {
          input: {
            id: conversationId,
            title,
          }
        }
      });
    } catch (error) {
      console.error('Error renaming conversation:', error);
      throw new Error('Failed to rename conversation');
    }
  }

  // Mark a conversation as unread (surfaces the unread dot again) — no dedicated flag, so we set unreadCount to 1
  static async markConversationAsUnread(conversationId: string): Promise<void> {
    try {
      await client.graphql({
        query: UPDATE_CONVERSATION,
        variables: {
          input: {
            id: conversationId,
            unreadCount: 1,
          }
        }
      });
    } catch (error) {
      console.error('Error marking conversation as unread:', error);
      throw new Error('Failed to mark conversation as unread');
    }
  }
}
